import React, { useEffect, useState, useCallback } from 'react';
import { CampaignSegment, FilterGroup } from '../../types';
import { fetchSegments, createSegment, updateSegment, deleteSegment } from '../../services/api';
import FilterBuilder from './FilterBuilder';

const emptyFilters: FilterGroup = { operator: 'AND', rules: [] };

const CampaignSegmentsPage: React.FC = () => {
  const [segments, setSegments]         = useState<CampaignSegment[]>([]);
  const [loading, setLoading]           = useState(true);
  const [showForm, setShowForm]         = useState(false);
  const [editing, setEditing]           = useState<CampaignSegment | null>(null);
  const [formName, setFormName]         = useState('');
  const [formDesc, setFormDesc]         = useState('');
  const [formEntity, setFormEntity]     = useState('lead');
  const [formFilters, setFormFilters]   = useState<FilterGroup>(emptyFilters);
  const [formChannel, setFormChannel]   = useState('whatsapp');
  const [saving, setSaving]             = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setSegments(await fetchSegments()); } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null); setFormName(''); setFormDesc(''); setFormEntity('lead');
    setFormFilters(emptyFilters); setShowForm(true);
  };

  const openEdit = (seg: CampaignSegment) => {
    setEditing(seg); setFormName(seg.name); setFormDesc(seg.description ?? '');
    setFormEntity(seg.entity_type); setFormFilters(seg.filters); setShowForm(true);
  };

  const handleSave = async () => {
    if (!formName.trim() || !formFilters.rules.length) return;
    setSaving(true);
    try {
      if (editing) {
        await updateSegment(editing.id, { name: formName, description: formDesc, entity_type: formEntity, filters: formFilters });
      } else {
        await createSegment({ name: formName, description: formDesc, entity_type: formEntity, filters: formFilters });
      }
      setShowForm(false);
      load();
    } catch { alert('Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this segment?')) return;
    try { await deleteSegment(id); load(); }
    catch { alert('Delete failed'); }
  };

  return (
    <div className="list-page">
      <div className="page-hd">
        <div className="page-hd-left">
          <h1 className="page-title">Audience Segments</h1>
          <p className="page-meta"><strong>{segments.length}</strong> saved segments</p>
        </div>
        <div className="page-hd-right">
          <button className="btn btn-primary" onClick={openNew}>+ New Segment</button>
        </div>
      </div>

      {loading ? <div className="spinner">Loading…</div> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
          {segments.map(seg => (
            <div key={seg.id} style={{ background: '#fff', borderRadius: 12, border: '1px solid var(--border)', padding: '16px 18px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--navy)' }}>{seg.name}</div>
                  {seg.description && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{seg.description}</div>}
                </div>
                <span style={{ padding: '2px 10px', background: '#ede9fe', color: '#6d28d9', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                  {seg.entity_type}s
                </span>
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#7c3aed', marginBottom: 4 }}>
                {seg.estimated_reach.toLocaleString('en-IN')}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
                matching {seg.entity_type}s &middot; {seg.filters.rules.length} filter rule{seg.filters.rules.length !== 1 ? 's' : ''}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="row-btn" onClick={() => openEdit(seg)}>Edit</button>
                <button className="row-btn row-btn-danger" onClick={() => handleDelete(seg.id)}>Delete</button>
              </div>
            </div>
          ))}
          {segments.length === 0 && (
            <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '60px 0', color: 'var(--text-muted)' }}>
              No segments yet. Click <strong>+ New Segment</strong> to define your first audience.
            </div>
          )}
        </div>
      )}

      {/* Form modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-box" style={{ maxWidth: 620 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing ? 'Edit Segment' : 'New Segment'}</h2>
              <button className="modal-close" onClick={() => setShowForm(false)}>×</button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label className="form-label">Segment Name *</label>
                <input className="form-input" value={formName} onChange={e => setFormName(e.target.value)} placeholder="e.g. Hot Punjab Leads" />
              </div>
              <div>
                <label className="form-label">Description</label>
                <input className="form-input" value={formDesc} onChange={e => setFormDesc(e.target.value)} />
              </div>
              <div>
                <label className="form-label">Target Entity</label>
                <select className="filter-select" value={formEntity} onChange={e => setFormEntity(e.target.value)}>
                  <option value="lead">Leads</option>
                  <option value="contact">Contacts</option>
                  <option value="customer">Customers</option>
                </select>
              </div>
              <div>
                <label className="form-label">Preview for Channel</label>
                <select className="filter-select" value={formChannel} onChange={e => setFormChannel(e.target.value)}>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="sms">SMS</option>
                  <option value="email">Email</option>
                </select>
              </div>
              <div>
                <label className="form-label">Filter Rules *</label>
                <FilterBuilder value={formFilters} onChange={setFormFilters} entityType={formEntity} channel={formChannel} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving || !formName.trim() || !formFilters.rules.length}>
                {saving ? 'Saving…' : editing ? 'Update Segment' : 'Create Segment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignSegmentsPage;
